import { createFileRoute, Link, notFound, redirect } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import {
  getPost,
  formatDate,
  authorFor,
  relatedPosts,
  POSTS,
  getSeo,
  getPrevNext,
  UNPUBLISHED_SLUGS,
} from "../content/posts";

// Send old links to the closest fuller article instead of a dead page.
const UNPUBLISHED_REDIRECTS: Record<string, string> = {
  "honours-vs-programme": "du-ba-economics-hons-vs-ba-programme-2026",
  "monopoly-price-discrimination-deadweight-loss-du":
    "microeconomics-sem-1-unit-6-monopoly-price-discrimination-deadweight-loss",
  "indifference-curve-budget-line-consumer-equilibrium-notes":
    "microeconomics-sem-1-unit-3-consumer-theory-indifference-curves-slutsky",
  "theory-of-production-returns-to-scale-cost-curves-notes":
    "microeconomics-sem-1-unit-4-production-costs-isoquants-returns-to-scale",
  "du-csas-round-2-schedule-revised-2026": "du-csas-round-3-mid-entry-schedule-2026",
  "du-csas-round-2-allotment-2026": "du-csas-round-3-mid-entry-schedule-2026",
  "du-internal-assessment-grace-marks-guidelines-2026": "du-internal-assessment-ia-strategy-2026",
};
import { categoryBySlug } from "../content/categories";
import { PostCard } from "../components/PostCard";
import { GradeUnderline } from "../components/GradeUnderline";
import { initSignatureAnimation } from "../lib/animations";

const SITE = "https://acadly.in";
const DEFAULT_OG = `${SITE}/og-default.png`;

function heroImageFor(slug: string): string {
  return `${SITE}/blog/${slug}.jpg`;
}

function slugify(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-");
}

export const Route = createFileRoute("/blog/$slug")({
  loader: ({ params }) => {
    if (UNPUBLISHED_SLUGS.has(params.slug)) {
      const to = UNPUBLISHED_REDIRECTS[params.slug];
      throw redirect(
        to ? { to: "/blog/$slug", params: { slug: to }, statusCode: 301 } : { to: "/blog", statusCode: 301 },
      );
    }
    const post = getPost(params.slug);
    if (!post) throw notFound();
    return { meta: post.meta };
  },
  head: ({ loaderData, params }) => {
    if (!loaderData) {
      return {
        meta: [
          { title: "Piece not found | Acadly" },
          { name: "robots", content: "noindex" },
        ],
      };
    }
    const { meta } = loaderData;
    const author = authorFor(meta.authorSlug);
    const seo = getSeo(params.slug);
    const canonical = `${SITE}/blog/${params.slug}`;
    const title = seo?.seoTitle ?? `${meta.title} | Acadly`;
    const description = seo?.metaDescription ?? meta.description;
    const ogImage = seo?.ogImage
      ? seo.ogImage.startsWith("http")
        ? seo.ogImage
        : `${SITE}${seo.ogImage}`
      : heroImageFor(params.slug);
    const keywords = [
      seo?.focusKeyword,
      ...(seo?.secondaryKeywords ?? []),
      ...(seo?.lsiKeywords ?? []),
      ...(meta.tags ?? []),
    ]
      .filter(Boolean)
      .join(", ");
    const modified = seo?.updatedDate ?? meta.updatedDate ?? meta.pubDate;

    return {
      meta: [
        { title },
        { name: "description", content: description },
        { name: "keywords", content: keywords },
        {
          name: "robots",
          content: "index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1",
        },
        { name: "author", content: author.name },
        { property: "og:site_name", content: "Acadly" },
        { property: "og:locale", content: "en_IN" },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "article" },
        { property: "og:url", content: canonical },
        { property: "og:image", content: ogImage },
        { property: "og:image:alt", content: meta.title },
        { property: "article:published_time", content: meta.pubDate },
        { property: "article:modified_time", content: modified },
        { property: "article:author", content: author.name },
        { property: "article:section", content: meta.category },
        ...(meta.tags ?? []).map((t) => ({ property: "article:tag", content: t })),
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
        { name: "twitter:image", content: ogImage },
        { name: "twitter:image:alt", content: meta.title },
      ],
      links: [{ rel: "canonical", href: canonical }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": meta.category === "news" ? "NewsArticle" : "BlogPosting",
            headline: meta.title,
            name: meta.title,
            description,
            image: [ogImage],
            datePublished: meta.pubDate,
            dateModified: modified,
            keywords,
            articleSection: meta.category,
            wordCount: meta.readMinutes * 220,
            inLanguage: "en-IN",
            url: canonical,
            author: {
              "@type": "Person",
              name: author.name,
              description: author.credentials,
              jobTitle: author.role,
              ...(author.portfolio ? { sameAs: [author.portfolio] } : {}),
            },
            publisher: {
              "@type": "Organization",
              name: "Acadly",
              url: SITE,
              logo: {
                "@type": "ImageObject",
                url: `${SITE}/logo.png`,
              },
            },
            mainEntityOfPage: canonical,
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
              { "@type": "ListItem", position: 2, name: "Blog", item: `${SITE}/blog` },
              {
                "@type": "ListItem",
                position: 3,
                name: categoryBySlug(meta.category)?.label ?? meta.category,
                item: `${SITE}/category/${meta.category}`,
              },
              {
                "@type": "ListItem",
                position: 4,
                name: meta.title,
                item: canonical,
              },
            ],
          }),
        },
        ...(seo?.faqs && seo.faqs.length > 0
          ? [
              {
                type: "application/ld+json",
                children: JSON.stringify({
                  "@context": "https://schema.org",
                  "@type": "FAQPage",
                  mainEntity: seo.faqs.map((f) => ({
                    "@type": "Question",
                    name: f.q,
                    acceptedAnswer: {
                      "@type": "Answer",
                      text: f.a,
                    },
                  })),
                }),
              },
            ]
          : []),
        ...(seo?.keyTakeaways &&
        seo.keyTakeaways.length > 0 &&
        (meta.category === "study-systems" || meta.category === "exam-strategy")
          ? [
              {
                type: "application/ld+json",
                children: JSON.stringify({
                  "@context": "https://schema.org",
                  "@type": "HowTo",
                  name: meta.title,
                  description: meta.description,
                  step: seo.keyTakeaways.map((t, idx) => ({
                    "@type": "HowToStep",
                    position: idx + 1,
                    text: t,
                  })),
                }),
              },
            ]
          : []),
      ],
    };
  },
  notFoundComponent: () => (
    <div className="mx-auto max-w-2xl px-5 py-24 text-center">
      <div className="stamp mx-auto mb-6">404 · Not found</div>
      <h1
        className="text-4xl font-semibold"
        style={{ fontFamily: "var(--font-display)" }}
      >
        No such piece.
      </h1>
      <p className="mt-4 text-pencil">
        The note you're looking for doesn't exist here.
      </p>
      <Link
        to="/blog"
        className="inline-block mt-6 mono-label border border-ink px-4 py-2 hover:text-grade hover:border-grade"
      >
        Back to the archive
      </Link>
    </div>
  ),
  component: PostPage,
});

function PostPage() {
  const { slug } = Route.useParams();
  const post = getPost(slug);
  if (!post) return null;
  const { meta, Body } = post;
  const author = authorFor(meta.authorSlug);
  const cat = categoryBySlug(meta.category);
  const related = relatedPosts(meta.slug, meta.category);
  const seo = getSeo(meta.slug);
  const { prev, next } = getPrevNext(meta.slug);
  const [toc, setToc] = useState<{ id: string; text: string; level: number }[]>([]);

  useEffect(() => {
    void initSignatureAnimation();
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [meta.slug]);

  // Auto-generate a Table of Contents by walking the rendered article for
  // h2/h3 elements, assigning stable ids, and building the TOC from them.
  useEffect(() => {
    const article = document.getElementById("post-body");
    if (!article) return;
    const headings = Array.from(article.querySelectorAll("h2, h3"));
    const used = new Set<string>();
    const items = headings.map((h) => {
      const text = h.textContent?.trim() ?? "";
      let id = slugify(text) || "section";
      let i = 2;
      while (used.has(id)) {
        id = `${slugify(text)}-${i++}`;
      }
      used.add(id);
      h.id = id;
      return { id, text, level: h.tagName === "H2" ? 2 : 3 };
    });
    setToc(items);
  }, [meta.slug]);

  return (
    <article>
      <header className="mx-auto max-w-3xl px-5 pt-14 pb-10 md:pt-20">
        <nav aria-label="Breadcrumb" className="mono-label mb-6">
          <Link to="/" className="hover:text-grade">Home</Link>
          <span aria-hidden className="px-2 text-rule">/</span>
          <Link to="/blog" className="hover:text-grade">Blog</Link>
          <span aria-hidden className="px-2 text-rule">/</span>
          <Link
            to="/category/$category"
            params={{ category: meta.category }}
            className="hover:text-grade"
          >
            {cat?.label ?? meta.category}
          </Link>
        </nav>

        <div className="stamp mb-6">{meta.heroEyebrow}</div>

        <h1
          className="text-4xl md:text-5xl leading-[1.05] tracking-tight font-semibold"
          style={{ fontFamily: "var(--font-display)" }}
        >
          <span className="relative inline-block">
            <span>{meta.title}</span>
            <GradeUnderline className="absolute left-0 -bottom-2" />
          </span>
        </h1>

        <p className="mt-6 text-lg text-pencil leading-relaxed">{meta.description}</p>

        <div className="mt-8 flex flex-wrap items-center gap-4 border-t border-rule pt-5">
          <div className="mono-label">By {author.name}</div>
          <span aria-hidden className="text-rule">·</span>
          <div className="mono-label">
            <time dateTime={meta.pubDate}>{formatDate(meta.pubDate)}</time>
          </div>
          {(seo?.updatedDate || meta.updatedDate) && (
            <>
              <span aria-hidden className="text-rule">·</span>
              <div className="mono-label">
                Updated{" "}
                <time dateTime={seo?.updatedDate ?? meta.updatedDate}>
                  {formatDate((seo?.updatedDate ?? meta.updatedDate)!)}
                </time>
              </div>
            </>
          )}
          <span aria-hidden className="text-rule">·</span>
          <div className="mono-label">{meta.readMinutes} min read</div>
          <span aria-hidden className="text-rule">·</span>
          <Link
            to="/category/$category"
            params={{ category: meta.category }}
            className="mono-label hover:text-grade"
          >
            {cat?.label ?? meta.category}
          </Link>
        </div>
      </header>

      <figure className="mx-auto max-w-4xl px-5 pb-6">
        <img
          src={`/blog/${meta.slug}.jpg`}
          alt={`Illustration for ${meta.title}`}
          width={1200}
          height={630}
          loading="eager"
          fetchPriority="high"
          className="w-full h-auto border border-rule bg-card"
        />
      </figure>

      {seo?.keyTakeaways && seo.keyTakeaways.length > 0 && (
        <aside
          aria-labelledby="key-takeaways"
          className="mx-auto max-w-3xl px-5 pb-6"
        >
          <div className="border border-ink bg-card p-6">
            <div className="mono-label mb-3">Key takeaways</div>
            <h2 id="key-takeaways" className="sr-only">
              Key takeaways
            </h2>
            <ul className="space-y-2 text-[15px] leading-relaxed">
              {seo.keyTakeaways.map((t, i) => (
                <li key={i} className="flex gap-3">
                  <span aria-hidden className="text-grade font-semibold">
                    ✓
                  </span>
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
        </aside>
      )}

      {toc.length > 2 && (
        <nav
          aria-label="Table of contents"
          className="mx-auto max-w-3xl px-5 pb-6"
        >
          <details className="border border-rule p-5" open>
            <summary className="mono-label cursor-pointer select-none">
              Table of contents
            </summary>
            <ol className="mt-4 space-y-1.5 text-[15px] list-decimal list-inside">
              {toc
                .filter((t) => t.level === 2)
                .map((t) => (
                  <li key={t.id}>
                    <a href={`#${t.id}`} className="hover:text-grade underline-offset-2 hover:underline">
                      {t.text}
                    </a>
                  </li>
                ))}
            </ol>
          </details>
        </nav>
      )}

      <div id="post-body" className="mx-auto max-w-3xl px-5 pb-16 prose-post">
        <Body />
      </div>

      {seo?.faqs && seo.faqs.length > 0 && (
        <section
          aria-labelledby="faq-heading"
          className="mx-auto max-w-3xl px-5 pb-14"
        >
          <div className="border-t border-ink pt-8">
            <div className="mono-label mb-2">Reader questions</div>
            <h2
              id="faq-heading"
              className="text-2xl md:text-3xl font-semibold mb-6"
              style={{ fontFamily: "var(--font-display)" }}
            >
              Frequently asked questions
            </h2>
            <div className="divide-y divide-rule border-t border-rule">
              {seo.faqs.map((f, i) => (
                <details key={i} className="py-4 group">
                  <summary className="cursor-pointer list-none flex justify-between items-start gap-4">
                    <h3 className="text-lg font-semibold pr-4">{f.q}</h3>
                    <span
                      aria-hidden
                      className="text-grade text-xl leading-none mt-1 transition-transform group-open:rotate-45"
                    >
                      +
                    </span>
                  </summary>
                  <p className="mt-3 text-pencil leading-relaxed">{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="mx-auto max-w-3xl px-5 pb-10">
        <div className="border-t border-ink pt-6">
          <div className="mono-label mb-3">Continue with a related piece</div>
          <div className="flex flex-wrap gap-2">
            <Link
              to="/category/$category"
              params={{ category: meta.category }}
              className="mono-label border border-ink px-3 py-1.5 hover:text-grade hover:border-grade"
            >
              More in {cat?.label ?? meta.category}
            </Link>
            {meta.tags.map((t) => (
              <span
                key={t}
                className="mono-label border border-rule px-3 py-1.5 text-pencil"
              >
                #{t}
              </span>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-3xl px-5 pb-14">
        <div className="border-t border-ink pt-6">
          <div className="mono-label mb-2">About the author</div>
          <div className="flex items-start gap-4">
            <div
              aria-hidden
              className="h-14 w-14 rounded-full bg-ink text-paper flex items-center justify-center text-lg font-semibold"
              style={{ fontFamily: "var(--font-display)" }}
            >
              {author.name
                .split(" ")
                .map((s) => s[0])
                .join("")
                .slice(0, 2)}
            </div>
            <div>
              <div className="font-semibold flex items-center gap-2">
                <span>{author.name}</span>
                {author.portfolio && (
                  <a
                    href={author.portfolio}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-pencil hover:text-grade underline mono-label"
                  >
                    Portfolio ↗
                  </a>
                )}
              </div>
              <div className="text-sm text-pencil">{author.role}</div>
              <p className="mt-1 text-sm text-pencil max-w-xl">{author.bio}</p>
            </div>
          </div>
        </div>
      </section>

      {(prev || next) && (
        <nav
          aria-label="Previous and next posts"
          className="mx-auto max-w-3xl px-5 pb-14"
        >
          <div className="grid gap-4 md:grid-cols-2 border-t border-ink pt-6">
            {prev ? (
              <Link
                to="/blog/$slug"
                params={{ slug: prev.slug }}
                className="block border border-rule p-4 hover:border-grade group"
                rel="prev"
              >
                <div className="mono-label mb-1 text-pencil group-hover:text-grade">
                  ← Previous
                </div>
                <div className="font-semibold leading-snug">{prev.title}</div>
              </Link>
            ) : (
              <div />
            )}
            {next ? (
              <Link
                to="/blog/$slug"
                params={{ slug: next.slug }}
                className="block border border-rule p-4 hover:border-grade text-right group"
                rel="next"
              >
                <div className="mono-label mb-1 text-pencil group-hover:text-grade">
                  Next →
                </div>
                <div className="font-semibold leading-snug">{next.title}</div>
              </Link>
            ) : (
              <div />
            )}
          </div>
        </nav>
      )}

      {related.length > 0 && (
        <section className="border-t border-ink bg-card">
          <div className="mx-auto max-w-6xl px-5 py-14">
            <h2
              className="text-2xl md:text-3xl font-semibold mb-8"
              style={{ fontFamily: "var(--font-display)" }}
            >
              Keep reading
            </h2>
            <div className="grid gap-10 md:grid-cols-3">
              {related.map((r) => (
                <PostCard key={r.meta.slug} meta={r.meta} />
              ))}
            </div>
            <div className="mt-10 text-center">
              <Link
                to="/blog"
                className="mono-label border border-ink px-4 py-2 hover:text-grade hover:border-grade"
              >
                Browse the full archive
              </Link>
            </div>
          </div>
        </section>
      )}

      <noscript>
        <div className="mx-auto max-w-3xl px-5 pb-10 text-sm text-pencil">
          {POSTS.length} more pieces at /blog.
        </div>
      </noscript>
    </article>
  );
}